/**
 * Employees Management Page
 * =========================
 * صفحة إدارة الموظفين
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';
import { useDialog } from '../../../context/DialogContext';

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
};

const EmployeesPage = () => {
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();
  const { confirm } = useDialog();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '', email: '', phone: '', department: '', job_title: '', base_salary: ''
  });

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filter !== 'all') params.append('status', filter);
      
      const data = await apiCall(`/tenant/employees?${params}`);
      setEmployees(data.items || []);
    } catch (err) {
      showError('فشل تحميل الموظفين');
    } finally {
      setLoading(false);
    }
  }, [apiCall, search, filter, showError]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await apiCall(`/tenant/employees/${editingEmployee.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        success('تم تحديث الموظف بنجاح');
      } else {
        await apiCall('/tenant/employees', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        success('تم إضافة الموظف بنجاح');
      }
      setShowModal(false);
      setEditingEmployee(null);
      setFormData({ full_name: '', email: '', phone: '', department: '', job_title: '', base_salary: '' });
      loadEmployees();
    } catch (err) {
      showError(err.message || 'فشلت العملية');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      full_name: employee.full_name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      department: employee.department || '',
      job_title: employee.job_title || '',
      base_salary: employee.base_salary || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'حذف الموظف', message: 'هل أنت متأكد من حذف هذا الموظف؟', type: 'delete', confirmText: 'حذف', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/tenant/employees/${id}`, { method: 'DELETE' });
      success('تم حذف الموظف بنجاح');
      loadEmployees();
    } catch (err) {
      showError('فشل حذف الموظف');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__title">
          <h1>إدارة الموظفين</h1>
          <p>{employees.length} موظف</p>
        </div>
        <button className="btn btn--primary" onClick={() => { setEditingEmployee(null); setFormData({ full_name: '', email: '', phone: '', department: '', job_title: '', base_salary: '' }); setShowModal(true); }}>
          {I.plus} <span>إضافة موظف</span>
        </button>
      </div>

      <div className="page-filters">
        <div className="search-box">
          {I.search}
          <input type="text" placeholder="بحث بالاسم أو الرقم..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
          <option value="all">جميع الحالات</option>
          <option value="active">نشط</option>
          <option value="inactive">غير نشط</option>
        </select>
      </div>

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : employees.length === 0 ? (
        <div className="page-empty">
          {I.user}
          <h3>لا يوجد موظفين</h3>
          <p>ابدأ بإضافة موظفين جدد</p>
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>الموظف</th>
                <th>القسم</th>
                <th>المسمى الوظيفي</th>
                <th>الهاتف</th>
                <th>الحالة</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(emp => (
                <tr key={emp.id}>
                  <td>
                    <div className="cell-user">
                      <div className="cell-user__avatar">{emp.full_name?.charAt(0)}</div>
                      <div className="cell-user__info">
                        <span className="cell-user__name">{emp.full_name}</span>
                        <span className="cell-user__email">{emp.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>{emp.department || '-'}</td>
                  <td>{emp.job_title || '-'}</td>
                  <td dir="ltr">{emp.phone || '-'}</td>
                  <td><span className={`badge badge--${emp.status === 'active' ? 'success' : 'danger'}`}>{emp.status === 'active' ? 'نشط' : 'غير نشط'}</span></td>
                  <td>
                    <div className="actions">
                      <button className="action-btn" onClick={() => handleEdit(emp)}>{I.edit}</button>
                      <button className="action-btn action-btn--danger" onClick={() => handleDelete(emp.id)}>{I.trash}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEmployee ? 'تعديل موظف' : 'إضافة موظف جديد'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>الاسم الكامل *</label>
                    <input type="text" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>البريد الإلكتروني</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>رقم الهاتف</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>القسم</label>
                    <input type="text" value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>المسمى الوظيفي</label>
                    <input type="text" value={formData.job_title} onChange={(e) => setFormData({...formData, job_title: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>الراتب الأساسي</label>
                    <input type="number" value={formData.base_salary} onChange={(e) => setFormData({...formData, base_salary: e.target.value})} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">{editingEmployee ? 'حفظ التعديلات' : 'إضافة'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage;
